import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { Accordion } from '@jutro/legacy/components';
import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import {
    WMICHOPolicyCoverages,
    WMICHODwellingCoverages,
    WMICHOWatercraftCoverages,
} from 'wmic-pe-capability-gateway-common-ho-react';
import appConfig from 'app-config';

import { useDocumentTitle, useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICScrollToError } from 'wmic-pe-components-platform-react';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';
import { FlowStepId } from 'wmic-pe-portals-utils-js';
import WMICHOCoverageUtil from 'wmic-pe-capability-gateway-common-ho-react/utils/WMICHOCoverageUtil';
import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import messages from './WMICHOCoveragesPage.messages';
import styles from './WMICHOCoveragesPage.module.scss';
import metadata from './WMICHOCoveragesPage.metadata.json5';
import { TranslatorContext } from '@jutro/legacy/locale';

const structureCustomQuote = (submissionVM, affectedQuote, clauses) =>
    // convert OfferingDTO to CustomQuotedDTO structure
    ({
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
    });

const getCustomQuote = (vm, lobPath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages,
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(lobOffering.coverages, lobName, null);
    }

    return structureCustomQuote(vm, lobOffering, clausesToUpdate);
};

const generateColumnData = submissionVM => {
    const lobOfferingPath = 'lobData.homeowners.offerings';

    const lobOfferings = _.get(submissionVM, `${lobOfferingPath}.value`);

    return lobOfferings.map((lobOffering, lobIndex) => ({
        name: lobOffering.branchName,
        code: lobOffering.branchCode,
        lob: {
            path: `${lobOfferingPath}.children[${lobIndex}]`,
            data: lobOffering,
        },
    }));
};

const getSelectedRiskId = (submissionVM, basePath) => {
    const riskPath = basePath.substring(0, basePath.indexOf('.coverages.children['));
    const riskVM = _.get(submissionVM, `${riskPath}.value`);
    return _.get(riskVM, 'publicID');
};

function WMICHOCoveragesPage(props) {
    const [showErrors, setShowErrors] = useState(false);
    const [scrollToError, setScrollToError] = useState(); // we toggle this when we want to scroll to the first error on the page

    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading } = useWizardModals();
    const { authHeader } = useAuthentication();
    const {
        disregardFieldValidation,
        registerInitialComponentValidation,
        initialValidation,
        onValidate,
        isComponentValid,
    } = useValidation('QuotePage');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useContext(TranslatorContext);

    const { wizardData: submissionVM, updateWizardData, underwritingIssues } = props;

    const initialized = useRef(false);

    useDocumentTitle('Coverages', submissionVM);

    // reset show errors to false whenever the component is back to a valid state or we stop editing
    useEffect(() => {
        if (isComponentValid) {
            setShowErrors(false);
        }
    }, [isComponentValid]);

    useEffect(() => {
        registerInitialComponentValidation(() =>
            WMICHOCoverageUtil.isDwellingSupportingCoverageDataValid(submissionVM)
        );
    }, []);

    useEffect(() => {
        initialized.current = true;
        // Store the initial SubmissionVM when component is mounted
    }, []);

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.HO_COVERAGES;
    }, [submissionVM.value]);

    const onUpdateCustomQuote = _basePath => {
        const lobPath = 'lobData.homeowners.offerings.children[0]';
        const lobName = ClausesUtil.getLobNameFromPath(lobPath);
        const customQuote = getCustomQuote(submissionVM, lobPath, lobName, true);
        return CustomQuoteService.updateCustomQuoteCoveragesAndQuoteData(
            customQuote,
            submissionVM.value,
            authHeader
        ).then(response => {
            const riskID = getSelectedRiskId(submissionVM, _basePath);
            const dwellingCoverageIndex = _.get(submissionVM, `${lobPath}.coverages.dwellingCoverages.value`).findIndex(
                dwellingCoverage => dwellingCoverage.publicID === riskID
            );
            const watercraftCoverageIndex = _.get(
                submissionVM,
                `${lobPath}.coverages.watercraftCoverages.value`
            ).findIndex(watercraftCoverage => watercraftCoverage.publicID === riskID);

            const newSubmissionVM = viewModelService.clone(submissionVM);
            newSubmissionVM.value = response;

            const removedFieldsFromLineCoverages = ClausesUtil.getRemovedClausesID(
                submissionVM,
                newSubmissionVM,
                `${lobPath}.coverages.lineCoverages`
            );
            const removedFieldsFromDwellingCoverages =
                dwellingCoverageIndex >= 0
                    ? ClausesUtil.getRemovedClausesID(
                          submissionVM,
                          newSubmissionVM,
                          `${lobPath}.coverages.dwellingCoverages.children[${dwellingCoverageIndex}].coverages`
                      )
                    : [];
            const removedFieldsFromWatercraftCoverages =
                watercraftCoverageIndex >= 0
                    ? ClausesUtil.getRemovedClausesID(
                          submissionVM,
                          newSubmissionVM,
                          `${lobPath}.coverages.watercraftCoverages.children[${watercraftCoverageIndex}].coverages`
                      )
                    : [];

            const allRemovedFields = [
                ...removedFieldsFromLineCoverages,
                ...removedFieldsFromDwellingCoverages,
                ...removedFieldsFromWatercraftCoverages,
            ];
            disregardFieldValidation(allRemovedFields);
            updateWizardData(newSubmissionVM);
        });
    };

    const syncCoverages = (value, changedPath) => {
        const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
        return onUpdateCustomQuote(basePath);
    };

    const changeSubmission = (value, changedPath) => {
        updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
    };

    const changeSubmissionAndSync = (value, changedPath) => {
        changeSubmission(value, changedPath);
        return syncCoverages(value, changedPath);
    };

    const columnData = generateColumnData(submissionVM);

    const commonOverrides = {
        coverageColumnData: columnData,
        quoteID: submissionVM.quoteID.value,
        changeSubmissionAndSync,
        changeSubmission,
        syncCoverages,
        underwritingIssues,
        onValidate,
        showErrors,
        readOnly: false,
    };

    const _iterableProps = () => {
        const dwellingOverrides = _.get(submissionVM, `${RISK_PATHS.DWELLINGS_PATH}.children`).map(
            (dwelling, index) => {
                const dwellingCoverages = WMICHOCoverageUtil.getDwellingCoveragesForRisk(
                    _.get(submissionVM, 'lobData.homeowners'),
                    dwelling.publicID.value
                );
                const dwellingOverride = {
                    [`dwellingCoveragesIterableComponent${index}`]: {
                        dwellingVM: dwelling,
                        dwellingCoverages,
                        dwellingIndex: index,
                        baseData: submissionVM.baseData,
                        jobVM: submissionVM,
                        ...commonOverrides,
                    },
                };

                return { ...dwellingOverride };
            }
        );

        const watercraftOverrides = _.get(submissionVM, `${RISK_PATHS.WATERCRAFT_PATH}.children`).map(
            (watercraft, index) => {
                const watercraftCoverages = WMICHOCoverageUtil.getWatercraftCoveragesForRisk(
                    _.get(submissionVM, 'lobData.homeowners'),
                    watercraft.publicID.value
                );
                const watercraftOverride = {
                    [`watercraftCoveragesIterableComponent${index}`]: {
                        watercraftVM: watercraft,
                        watercraftCoverages,
                        watercraftIndex: index,
                        ...commonOverrides,
                    },
                };
                return { ...watercraftOverride };
            }
        );

        return Object.assign({}, ...dwellingOverrides, ...watercraftOverrides);
    };

    const overrideProps = {
        policyCoveragesContainer: {
            jobVM: submissionVM,
            lineCoverages: _.get(submissionVM, 'lobData.homeowners.offerings.children[0].coverages.lineCoverages'),
            ...commonOverrides,
        },
        dwellingCoveragesIterableAccordionContainer: {
            data: _.get(submissionVM, `${RISK_PATHS.DWELLINGS_PATH}.children`),
        },
        watercraftCoveragesIterableAccordionContainer: {
            visible: _.get(submissionVM, `${RISK_PATHS.WATERCRAFT_PATH}.children`, []).length > 0,
            data: _.get(submissionVM, `${RISK_PATHS.WATERCRAFT_PATH}.children`),
        },
        ..._iterableProps(),
    };

    const onNext = useCallback(async () => {
        try {
            const isDwellingBeingEdited = _.some(
                _.get(submissionVM, `${RISK_PATHS.DWELLINGS_PATH}.value`),
                dwelling => dwelling.isUnderEditing
            );
            if (!isComponentValid || isDwellingBeingEdited) {
                setShowErrors(true);
                setScrollToError(Date.now()); // force scroll to first error
                return false;
            }
            setWizardLoading(true);
            const newSubmissionVM = viewModelService.clone(submissionVM);
            _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmissionWithLOBData(
                newSubmissionVM.value,
                authHeader
            );
            updateWizardData(submissionVM);
            return submissionVM;
        } catch (error) {
            WMICErrorHandler.processAsModal(error);
            return submissionVM;
        } finally {
            setWizardLoading(false);
        }
    }, [LoadSaveService, authHeader, isComponentValid, setWizardLoading, submissionVM, viewModelService]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onChangeSubmission: changeSubmission,
            onSyncCoverages: syncCoverages,
        },
        resolveComponentMap: {
            WMICHODwellingCoverages,
            WMICHOPolicyCoverages,
            WMICHOWatercraftCoverages,
            Accordion,
        },
    };

    return initialized ? (
        <WMICWizardSubmissionPage
            onNext={onNext}
            skipWhen={initialValidation}
            cancelLabel={translator(commonMessages.saveAndExit)}
            flowStepId={FlowStepId.HO_COVERAGES}
        >
            <WMICScrollToError counter={scrollToError} />
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                showErrors={showErrors}
            />
        </WMICWizardSubmissionPage>
    ) : null;
}

WMICHOCoveragesPage.propTypes = wizardProps;
export default WMICHOCoveragesPage;
